import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Menu, X, Phone, LogIn, Facebook, Instagram } from "lucide-react";
import LanguageSwitcher from "./LanguageSwitcher";

const TikTokIcon = ({ className = "w-4 h-4" }) => (
  <svg viewBox="0 0 24 24" fill="currentColor" className={className} aria-hidden="true">
    <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5.8 20.1a6.34 6.34 0 0 0 10.86-4.43V8.93a8.16 8.16 0 0 0 4.78 1.52V7a4.85 4.85 0 0 1-1.85-.31z"/>
  </svg>
);

const LINKS = [
  { href: "#accueil", label: "Accueil" },
  { href: "#tarifs", label: "Tarifs" },
  { to: "/moto", label: "Moto" },
  { href: "#elearning", label: "Points de rendez-vous" },
  { to: "/blog", label: "Blog" },
  { href: "#contact", label: "Contact" },
];

const FB_URL = "https://www.facebook.com/people/Dynastie-Drive-School-Boulogne-Billancourt/";
const IG_URL = "https://www.instagram.com/ddsbillancourt";
const TT_URL = "https://www.tiktok.com/@dds.billancourt";

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 16);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <header
      data-testid="site-navbar"
      className={`sticky top-0 inset-x-0 z-50 transition-all duration-300 ${
        scrolled
          ? "bg-white/95 backdrop-blur-xl border-b border-black/5 shadow-sm"
          : "bg-white/80 backdrop-blur"
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 md:px-8 h-20 flex items-center justify-between">
        <a href="#accueil" className="flex items-center gap-3 shrink-0 text-[#1a2332] font-headings font-black text-xl tracking-tight" data-testid="logo-link">
          <img src="/brand/logo.png" alt="Logo auto-école DDSBillancourt Boulogne-Billancourt" className="w-11 h-11 rounded-xl object-contain" />
          <span className="hidden xl:inline">DDSBillancourt</span>
        </a>

        <nav className="hidden lg:flex items-center gap-5 xl:gap-7 px-4">
          {LINKS.map((l) => (
            l.to ? (
              <Link
                key={l.to}
                to={l.to}
                data-testid={`nav-link-${l.label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                className="text-[#1a2332]/75 hover:text-[#00a86b] font-headings font-semibold text-sm tracking-wide transition-colors"
              >
                {l.label}
              </Link>
            ) : (
              <a
                key={l.href}
                href={`/${l.href}`}
                data-testid={`nav-link-${l.label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                className="text-[#1a2332]/75 hover:text-[#00a86b] font-headings font-semibold text-sm tracking-wide transition-colors"
              >
                {l.label}
              </a>
            )
          ))}
        </nav>

        <div className="hidden lg:flex items-center gap-3">
          <a href={FB_URL} target="_blank" rel="noopener noreferrer" aria-label="Facebook DDSBillancourt" data-testid="nav-facebook"
            className="w-9 h-9 rounded-full border border-[#1a2332]/15 flex items-center justify-center text-[#1a2332]/70 hover:text-[#00a86b] hover:border-[#00a86b] transition-colors">
            <Facebook className="w-4 h-4" />
          </a>
          <a href={IG_URL} target="_blank" rel="noopener noreferrer" aria-label="Instagram DDSBillancourt" data-testid="nav-instagram"
            className="w-9 h-9 rounded-full border border-[#1a2332]/15 flex items-center justify-center text-[#1a2332]/70 hover:text-[#FF8C42] hover:border-[#FF8C42] transition-colors">
            <Instagram className="w-4 h-4" />
          </a>
          <a href={TT_URL} target="_blank" rel="noopener noreferrer" aria-label="TikTok DDSBillancourt" data-testid="nav-tiktok"
            className="w-9 h-9 rounded-full border border-[#1a2332]/15 flex items-center justify-center text-[#1a2332]/70 hover:text-[#1a2332] hover:border-[#1a2332] transition-colors">
            <TikTokIcon className="w-4 h-4" />
          </a>
          <a
            href="tel:+33141315061"
            data-testid="nav-phone"
            className="flex items-center gap-2 text-[#1a2332]/80 hover:text-[#00a86b] font-semibold text-sm"
          >
            <Phone className="w-4 h-4" /> 01 41 31 50 61
          </a>
          <LanguageSwitcher />
          <Link
            to="/login"
            data-testid="nav-espace-membre"
            className="inline-flex items-center gap-1.5 border border-[#1a2332]/20 hover:border-[#00a86b] hover:text-[#00a86b] text-[#1a2332] font-headings font-bold rounded-xl px-4 py-2.5 text-sm transition-colors"
          >
            <LogIn className="w-4 h-4" /> Espace membre
          </Link>
          <a
            href="#contact"
            data-testid="nav-cta-inscription"
            className="bg-[#FF8C42] hover:bg-[#e8763a] text-white font-headings font-bold rounded-xl px-5 py-2.5 text-sm shadow-md hover:-translate-y-0.5 transition-all"
          >
            S'inscrire
          </a>
        </div>

        <button
          data-testid="mobile-menu-toggle"
          onClick={() => setOpen((v) => !v)}
          className="lg:hidden text-[#1a2332] p-2 rounded-lg hover:bg-black/5"
          aria-label="Menu"
        >
          {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
        </button>
      </div>

      {open && (
        <div className="lg:hidden bg-white border-t border-black/5 shadow-lg">
          <div className="max-w-7xl mx-auto px-6 py-6 flex flex-col gap-3">
            {LINKS.map((l) => (
              l.to ? (
                <Link
                  key={l.to}
                  to={l.to}
                  onClick={() => setOpen(false)}
                  data-testid={`mobile-nav-link-${l.label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                  className="text-[#1a2332] font-headings font-semibold text-base py-2 border-b border-black/5"
                >
                  {l.label}
                </Link>
              ) : (
                <a
                  key={l.href}
                  href={`/${l.href}`}
                  onClick={() => setOpen(false)}
                  data-testid={`mobile-nav-link-${l.label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                  className="text-[#1a2332] font-headings font-semibold text-base py-2 border-b border-black/5"
                >
                  {l.label}
                </a>
              )
            ))}
            <a
              href="#contact"
              onClick={() => setOpen(false)}
              className="mt-2 bg-[#FF8C42] text-white text-center font-headings font-bold rounded-xl px-5 py-3"
              data-testid="mobile-cta-inscription"
            >
              S'inscrire
            </a>
            <Link
              to="/login"
              onClick={() => setOpen(false)}
              className="border border-[#1a2332]/20 text-[#1a2332] text-center font-headings font-bold rounded-xl px-5 py-3"
              data-testid="mobile-espace-membre"
            >
              Espace membre
            </Link>
            <LanguageSwitcher mobile />
            <div className="flex items-center justify-center gap-4 pt-3">
              <a href={FB_URL} target="_blank" rel="noopener noreferrer" aria-label="Facebook" data-testid="mobile-facebook"
                className="w-11 h-11 rounded-full border border-[#1a2332]/15 flex items-center justify-center text-[#00a86b]">
                <Facebook className="w-5 h-5" />
              </a>
              <a href={IG_URL} target="_blank" rel="noopener noreferrer" aria-label="Instagram" data-testid="mobile-instagram"
                className="w-11 h-11 rounded-full border border-[#1a2332]/15 flex items-center justify-center text-[#FF8C42]">
                <Instagram className="w-5 h-5" />
              </a>
              <a href={TT_URL} target="_blank" rel="noopener noreferrer" aria-label="TikTok" data-testid="mobile-tiktok"
                className="w-11 h-11 rounded-full border border-[#1a2332]/15 flex items-center justify-center text-[#1a2332]">
                <TikTokIcon className="w-5 h-5" />
              </a>
            </div>
          </div>
        </div>
      )}
    </header>
  );
}
